import React, { useState, useEffect } from "react";
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Switch,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Chip,
  Collapse,
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Save,
  Trash,
} from "lucide-react";
import ProductSection from "../ProductSection";

// Common sizes for different product types
const COMMON_SIZES = {
  FOOTWEAR: ["6", "7", "8", "9", "10", "11", "12"],
  APPAREL: ["XS", "S", "M", "L", "XL", "XXL"],
  ACCESSORIES: ["ONE SIZE"],
};

const ProductVariants = ({ productData, updateProductData, errors }) => {
  const [showAddVariant, setShowAddVariant] = useState(false);
  const [variantData, setVariantData] = useState({
    variantCode: "",
    size: "",
    batchTracking: false,
    pricing: {
      mrp: 0,
      discount: 0,
      sellingPrice: 0,
      purchasePrice: 0,
    },
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedVariant, setExpandedVariant] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  const [batchData, setBatchData] = useState({
    batchNumber: "",
    quantity: "",
    pricing: {
      mrp: "",
      discount: "",
      sellingPrice: "",
      purchasePrice: "",
    },
  });

  const handleVariantTypeChange = event => {
    const hasVariants = event.target.value === "multiple";
    updateProductData({
      hasVariants,
      isSingleSize: !hasVariants,
      variants: [],
    });
  };

  const handleSingleSizeChange = event => {
    updateProductData({
      defaultSize: event.target.value,
    });
  };

  const handleBatchTrackingChange = event => {
    updateProductData({
      batchTracking: event.target.checked,
    });
  };

  const handleVariantChange = (field, value, type = "text") => {
    let processedValue = value;
    if (type === "number") {
      processedValue = parseFloat(value) || 0;
    }

    setVariantData(prev => {
      const updatedData = { ...prev };

      // Handle nested properties like pricing.mrp
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        updatedData[parent] = {
          ...updatedData[parent],
          [child]: processedValue,
        };
      } else {
        updatedData[field] = processedValue;
      }

      // Auto-calculate selling price when MRP or discount changes
      if (field === "pricing.mrp" || field === "pricing.discount") {
        const mrp =
          field === "pricing.mrp" ? processedValue : updatedData.pricing.mrp;
        const discount =
          field === "pricing.discount"
            ? processedValue
            : updatedData.pricing.discount;
        updatedData.pricing.sellingPrice = calculateSellingPrice(mrp, discount);
      }

      return updatedData;
    });
  };

  const calculateSellingPrice = (mrp, discount) => {
    const discountAmount = (mrp * discount) / 100;
    return Math.max(0, mrp - discountAmount).toFixed(2);
  };

  // Calculate selling price whenever MRP or discount changes
  useEffect(() => {
    if (variantData.pricing.mrp && variantData.pricing.discount) {
      const discountAmount =
        (variantData.pricing.mrp * variantData.pricing.discount) / 100;
      const newSellingPrice = variantData.pricing.mrp - discountAmount;

      // Only update if the user hasn't manually changed the selling price
      if (
        !variantData.pricing.sellingPrice ||
        variantData.pricing.sellingPrice === 0
      ) {
        setVariantData(prev => ({
          ...prev,
          pricing: {
            ...prev.pricing,
            sellingPrice: newSellingPrice,
          },
        }));
      }
    }
  }, [
    variantData.pricing.mrp,
    variantData.pricing.discount,
    variantData.pricing.sellingPrice,
  ]);

  const handleAddVariant = () => {
    if (editingIndex !== null) {
      // Update existing variant
      const updatedVariants = [...productData.variants];
      updatedVariants[editingIndex] = variantData;
      updateProductData({ variants: updatedVariants });
      setEditingIndex(null);
    } else {
      // Add new variant
      updateProductData({
        variants: [...productData.variants, variantData],
      });
    }

    // Reset form
    setVariantData({
      variantCode: "",
      size: "",
      batchTracking: false,
      pricing: {
        mrp: 0,
        discount: 0,
        sellingPrice: 0,
        purchasePrice: 0,
      },
    });
    setShowAddVariant(false);
  };

  const handleEditVariant = index => {
    setVariantData(productData.variants[index]);
    setEditingIndex(index);
    setShowAddVariant(true);
    setExpandedVariant(null);
    setEditingVariant(index);
  };

  const handleSaveVariant = index => {
    const updatedVariants = [...productData.variants];
    updatedVariants[index] = {
      ...variantData,
      batches: updatedVariants[index].batches || [],
    };
    updateProductData({ variants: updatedVariants });
    setEditingVariant(null);

    // Reset variant form
    setVariantData({
      variantCode: "",
      size: "",
      batchTracking: false,
      pricing: {
        mrp: 0,
        discount: 0,
        sellingPrice: 0,
        purchasePrice: 0,
      },
    });
  };

  const handleDeleteVariant = index => {
    const updatedVariants = [...productData.variants];
    updatedVariants.splice(index, 1);
    updateProductData({ variants: updatedVariants });
  };

  const handleQuickAddSize = size => {
    if (!productData.variants.some(v => v.size === size)) {
      setVariantData({
        ...variantData,
        size,
        variantCode: `${productData.productCode || "PROD"}-${size}`,
      });
      setShowAddVariant(true);
    }
  };

  const toggleExpandVariant = index => {
    setExpandedVariant(expandedVariant === index ? null : index);
  };

  // Batch handling functions
  const openBatchDialog = index => {
    setSelectedVariantIndex(index);
    setBatchData({
      batchNumber: "",
      quantity: 0,
      pricing: {
        mrp: productData.variants[index].pricing.mrp || 0,
        discount: productData.variants[index].pricing.discount || 0,
        sellingPrice: productData.variants[index].pricing.sellingPrice || 0,
        purchasePrice: productData.variants[index].pricing.purchasePrice || 0,
      },
    });
    setBatchDialogOpen(true);
  };

  const handleBatchChange = (field, value, type = "text") => {
    let processedValue = value;
    if (type === "number") {
      processedValue = parseFloat(value) || 0;
    }

    setBatchData(prev => {
      const updatedData = { ...prev, [field]: processedValue };

      // Auto-calculate selling price when MRP or discount changes
      if (field === "mrp" || field === "discount") {
        const mrp = field === "mrp" ? processedValue : prev.mrp;
        const discount = field === "discount" ? processedValue : prev.discount;
        updatedData.sellingPrice = calculateSellingPrice(mrp, discount);
      }

      return updatedData;
    });
  };

  const handleAddBatch = () => {
    if (!batchData.batchNumber) return;

    const updatedVariants = [...productData.variants];
    const variant = updatedVariants[selectedVariantIndex];

    variant.batches = [...(variant.batches || []), { ...batchData }];

    updateProductData({ variants: updatedVariants });
    setBatchDialogOpen(false);
  };

  const handleDeleteBatch = (variantIndex, batchIndex) => {
    const updatedVariants = [...productData.variants];
    updatedVariants[variantIndex].batches.splice(batchIndex, 1);
    updateProductData({ variants: updatedVariants });
  };

  return (
    <div className="space-y-6">
      <ProductSection data="Product Variant Configuration" />

      <div className="bg-gray-50 p-4 rounded-lg">
        <FormControl component="fieldset">
          <RadioGroup
            name="variantType"
            value={productData.hasVariants ? "multiple" : "single"}
            onChange={handleVariantTypeChange}
            row>
            <FormControlLabel
              value="single"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": { color: "#5652B0" },
                  }}
                />
              }
              label={<span className="font-outfit text-sm">Single Size</span>}
            />
            <FormControlLabel
              value="multiple"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": { color: "#5652B0" },
                  }}
                />
              }
              label={
                <span className="font-outfit text-sm">Multiple Variants</span>
              }
            />
          </RadioGroup>
        </FormControl>
      </div>

      {productData.isSingleSize ? (
        <div className="space-y-4">
          <ProductSection data="Single Size Configuration" />

          <div className="border border-gray-200 rounded-lg p-4">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  label="Size"
                  name="defaultSize"
                  value={productData.defaultSize}
                  onChange={handleSingleSizeChange}
                  fullWidth
                  size="small"
                  error={!!errors.defaultSize}
                  helperText={errors.defaultSize}
                  required
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                      fontSize: "0.875rem",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={productData.batchTracking}
                      onChange={handleBatchTrackingChange}
                      color="secondary"
                    />
                  }
                  label={
                    <span className="font-outfit text-sm">
                      Enable Batch Tracking
                    </span>
                  }
                />
              </Grid>
            </Grid>

            {productData.batchTracking && (
              <div className="bg-blue-50 p-3 rounded-lg mt-3">
                <p className="text-blue-700 font-outfit text-xs">
                  Batch tracking is enabled. You will be able to add batch
                  details in the next step.
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <ProductSection data="Product Variants" />

            <button
              onClick={() => {
                setShowAddVariant(true);
                setEditingIndex(null);
                setVariantData({
                  variantCode: "",
                  size: "",
                  batchTracking: false,
                  pricing: {
                    mrp: 0,
                    discount: 0,
                    sellingPrice: 0,
                    purchasePrice: 0,
                  },
                });
              }}
              className="px-3 py-1.5 bg-[#5652B0] text-white rounded-lg text-xs font-medium hover:bg-[#4A47A3] flex items-center gap-1">
              <Plus size={14} />
              Add Variant
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            {/* Quick add common sizes */}
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-2">
                Quick Add Common Sizes:
              </p>
              <div className="flex flex-wrap gap-1">
                {COMMON_SIZES.APPAREL.map(size => (
                  <Chip
                    key={size}
                    label={size}
                    size="small"
                    onClick={() => handleQuickAddSize(size)}
                    sx={{
                      fontFamily: "Outfit",
                      fontSize: "0.75rem",
                      backgroundColor: "#F9F8FE",
                      color: "#5652B0",
                      "&:hover": {
                        backgroundColor: "#EEEDF7",
                      },
                    }}
                  />
                ))}
              </div>
            </div>

            {productData.variants.length === 0 && !showAddVariant ? (
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-gray-500 text-sm mb-2">
                  No variants added yet
                </p>
                <button
                  onClick={() => {
                    setShowAddVariant(true);
                    setEditingIndex(null);
                  }}
                  className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE]">
                  Add Your First Variant
                </button>
              </div>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        Variant Code
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        Size
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        MRP (₹)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        Discount
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        Selling Price (₹)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        Purchase Price (₹)
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}>
                        Batch Tracking
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "120px",
                        }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productData.variants.map((variant, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {editingVariant === index ? (
                              <TextField
                                value={variantData.variantCode}
                                onChange={e =>
                                  handleVariantChange(
                                    "variantCode",
                                    e.target.value,
                                  )
                                }
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  sx: {
                                    borderRadius: "0.5rem",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : (
                              variant.variantCode
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {editingVariant === index ? (
                              <TextField
                                value={variantData.size}
                                onChange={e =>
                                  handleVariantChange("size", e.target.value)
                                }
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  sx: {
                                    borderRadius: "0.5rem",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : (
                              variant.size
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {editingVariant === index ? (
                              <TextField
                                placeholder="MRP"
                                type="number"
                                value={variantData.pricing.mrp}
                                onChange={e =>
                                  handleVariantChange(
                                    "pricing.mrp",
                                    e.target.value,
                                    "number",
                                  )
                                }
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₹
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    borderRadius: "0.5rem",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : (
                              `₹${variant.pricing?.mrp || 0}`
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {editingVariant === index ? (
                              <TextField
                                placeholder="Discount"
                                type="number"
                                value={variantData.pricing.discount}
                                onChange={e =>
                                  handleVariantChange(
                                    "pricing.discount",
                                    e.target.value,
                                    "number",
                                  )
                                }
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      %
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    borderRadius: "0.5rem",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : (
                              `${variant.pricing?.discount || 0}%`
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {editingVariant === index ? (
                              <TextField
                                placeholder="Selling Price"
                                type="number"
                                value={variantData.pricing.sellingPrice}
                                onChange={e =>
                                  handleVariantChange(
                                    "pricing.sellingPrice",
                                    e.target.value,
                                    "number",
                                  )
                                }
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₹
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    borderRadius: "0.5rem",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : (
                              `₹${variant.pricing?.sellingPrice || 0}`
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {editingVariant === index ? (
                              <TextField
                                placeholder="Purchase Price"
                                type="number"
                                value={variantData.pricing.purchasePrice}
                                onChange={e =>
                                  handleVariantChange(
                                    "pricing.purchasePrice",
                                    e.target.value,
                                    "number",
                                  )
                                }
                                size="small"
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₹
                                    </InputAdornment>
                                  ),
                                  sx: {
                                    borderRadius: "0.5rem",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            ) : (
                              `₹${variant.pricing?.purchasePrice || 0}`
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ fontFamily: "Outfit", fontSize: "0.75rem" }}>
                            {variant.batchTracking ? "Yes" : "No"}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {editingVariant === index ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveVariant(index)}
                                  sx={{ color: "#5652B0", padding: "4px" }}>
                                  <Save size={14} />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditVariant(index)}
                                  sx={{ color: "#5652B0", padding: "4px" }}>
                                  <Edit size={14} />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteVariant(index)}
                                sx={{ color: "#d32f2f", padding: "4px" }}>
                                <Trash2 size={14} />
                              </IconButton>
                              {variant.batchTracking && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleExpandVariant(index)}
                                  sx={{ color: "#5652B0", padding: "4px" }}>
                                  {expandedVariant === index ? (
                                    <ChevronUp size={14} />
                                  ) : (
                                    <ChevronDown size={14} />
                                  )}
                                </IconButton>
                              )}
                              {variant.batchTracking && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openBatchDialog(index)}
                                  sx={{
                                    borderColor: "#5652B0",
                                    color: "#5652B0",
                                    fontFamily: "Outfit",
                                    fontSize: "0.75rem",
                                    "&:hover": {
                                      borderColor: "#4A47A3",
                                      backgroundColor: "#F9F8FE",
                                    },
                                  }}>
                                  Add Batch
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {variant.batchTracking && expandedVariant === index && (
                          <TableRow>
                            <TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={6}>
                              <Collapse
                                in={expandedVariant === index}
                                timeout="auto"
                                unmountOnExit>
                                <Box
                                  sx={{
                                    margin: 1,
                                    padding: 1,
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "0.5rem",
                                  }}>
                                  <div className="text-xs font-medium text-gray-700 mb-2">
                                    Batches
                                  </div>
                                  {variant.batches &&
                                  variant.batches.length > 0 ? (
                                    <TableContainer>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow
                                            sx={{ backgroundColor: "#F9F8FE" }}>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              Batch Number
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              Quantity
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              MRP
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              Discount
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              Selling Price
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              Purchase Price
                                            </TableCell>
                                            <TableCell
                                              sx={{
                                                fontFamily: "Outfit",
                                                fontWeight: 600,
                                              }}>
                                              Actions
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {variant.batches.map(
                                            (batch, batchIndex) => (
                                              <TableRow key={batchIndex}>
                                                <TableCell
                                                  sx={{ fontFamily: "Outfit" }}>
                                                  {batch.batchNumber}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontFamily: "Outfit" }}>
                                                  {batch.quantity}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontFamily: "Outfit" }}>
                                                  ₹{batch.pricing.mrp}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontFamily: "Outfit" }}>
                                                  {batch.pricing.discount}%
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontFamily: "Outfit" }}>
                                                  ₹{batch.pricing.sellingPrice}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontFamily: "Outfit" }}>
                                                  ₹{batch.pricing.purchasePrice}
                                                </TableCell>
                                                <TableCell>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      handleDeleteBatch(
                                                        index,
                                                        batchIndex,
                                                      )
                                                    }
                                                    sx={{ color: "#FF5252" }}>
                                                    <Trash size={16} />
                                                  </IconButton>
                                                </TableCell>
                                              </TableRow>
                                            ),
                                          )}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      className="font-outfit text-gray-500 italic">
                                      No batches added yet. Click "Add Batch" to
                                      add a new batch.
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}

                    {showAddVariant && (
                      <TableRow>
                        <TableCell>
                          <TextField
                            placeholder="Variant Code"
                            value={variantData.variantCode}
                            onChange={e =>
                              handleVariantChange("variantCode", e.target.value)
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Size"
                            value={variantData.size}
                            onChange={e =>
                              handleVariantChange("size", e.target.value)
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="MRP"
                            type="number"
                            value={variantData.pricing.mrp}
                            onChange={e =>
                              handleVariantChange(
                                "pricing.mrp",
                                e.target.value,
                                "number",
                              )
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₹
                                </InputAdornment>
                              ),
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Discount"
                            type="number"
                            value={variantData.pricing.discount}
                            onChange={e =>
                              handleVariantChange(
                                "pricing.discount",
                                e.target.value,
                                "number",
                              )
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  %
                                </InputAdornment>
                              ),
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Selling Price"
                            type="number"
                            value={variantData.pricing.sellingPrice}
                            onChange={e =>
                              handleVariantChange(
                                "pricing.sellingPrice",
                                e.target.value,
                                "number",
                              )
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₹
                                </InputAdornment>
                              ),
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            placeholder="Purchase Price"
                            type="number"
                            value={variantData.pricing.purchasePrice}
                            onChange={e =>
                              handleVariantChange(
                                "pricing.purchasePrice",
                                e.target.value,
                                "number",
                              )
                            }
                            size="small"
                            fullWidth
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₹
                                </InputAdornment>
                              ),
                              sx: {
                                borderRadius: "0.5rem",
                                fontFamily: "Outfit",
                                fontSize: "0.75rem",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={variantData.batchTracking}
                            onChange={e =>
                              handleVariantChange(
                                "batchTracking",
                                e.target.checked,
                                "checkbox",
                              )
                            }
                            color="secondary"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <IconButton
                              size="small"
                              onClick={() => {
                                // Show additional pricing fields
                                setExpandedVariant("new");
                              }}
                              sx={{
                                color: "#5652B0",
                                padding: "4px",
                              }}>
                              {expandedVariant === "new" ? (
                                <ChevronUp size={14} />
                              ) : (
                                <ChevronDown size={14} />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleAddVariant}
                              disabled={
                                !variantData.variantCode ||
                                !variantData.size ||
                                !variantData.pricing.mrp
                              }
                              sx={{
                                color: "#5652B0",
                                padding: "4px",
                                "&.Mui-disabled": {
                                  color: "rgba(0, 0, 0, 0.26)",
                                },
                              }}>
                              <Check size={14} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setShowAddVariant(false);
                                setEditingIndex(null);
                                setExpandedVariant(null);
                              }}
                              sx={{ color: "#d32f2f", padding: "4px" }}>
                              <X size={14} />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {showAddVariant && expandedVariant === "new" && (
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={6}>
                          <Collapse
                            in={expandedVariant === "new"}
                            timeout="auto"
                            unmountOnExit>
                            <Box
                              sx={{
                                margin: 1,
                                padding: 1,
                                backgroundColor: "#f9fafb",
                                borderRadius: "0.5rem",
                              }}>
                              <div className="text-xs font-medium text-gray-700 mb-2">
                                Additional Pricing Details
                              </div>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Discount (%)"
                                    type="number"
                                    value={variantData.pricing.discount}
                                    onChange={e =>
                                      handleVariantChange(
                                        "pricing.discount",
                                        e.target.value,
                                        "number",
                                      )
                                    }
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          %
                                        </InputAdornment>
                                      ),
                                      sx: {
                                        borderRadius: "0.5rem",
                                        fontFamily: "Outfit",
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        fontFamily: "Outfit",
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Purchase Price (₹)"
                                    type="number"
                                    value={variantData.pricing.purchasePrice}
                                    onChange={e =>
                                      handleVariantChange(
                                        "pricing.purchasePrice",
                                        e.target.value,
                                        "number",
                                      )
                                    }
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          ₹
                                        </InputAdornment>
                                      ),
                                      sx: {
                                        borderRadius: "0.5rem",
                                        fontFamily: "Outfit",
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                    InputLabelProps={{
                                      sx: {
                                        fontFamily: "Outfit",
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>

                              {variantData.batchTracking && (
                                <div className="mt-3 text-xs text-blue-700 bg-blue-50 p-2 rounded">
                                  Batch tracking is enabled for this variant.
                                  You can manage batches after creating the
                                  product.
                                </div>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {!showAddVariant && productData.variants.length > 0 && (
              <button
                onClick={() => {
                  setShowAddVariant(true);
                  setEditingIndex(null);
                  setVariantData({
                    variantCode: "",
                    size: "",
                    batchTracking: false,
                    pricing: {
                      mrp: 0,
                      discount: 0,
                      sellingPrice: 0,
                      purchasePrice: 0,
                    },
                  });
                }}
                className="px-3 py-1.5 border border-[#5652B0] text-[#5652B0] rounded-lg text-xs font-medium hover:bg-[#F9F8FE] mt-3 flex items-center gap-1">
                <Plus size={14} />
                Add Variant
              </button>
            )}
          </div>

          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-gray-700 font-outfit text-xs">
              <strong>Note:</strong> For variants with batch tracking enabled,
              you will be able to add batch details after creating the product.
            </p>
          </div>
        </div>
      )}

      {/* Batch Dialog */}
      <Dialog
        open={batchDialogOpen}
        onClose={() => setBatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ fontFamily: "Outfit", fontWeight: 600 }}>
          Add Batch
          <IconButton
            aria-label="close"
            onClick={() => setBatchDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Batch Number"
                  fullWidth
                  size="small"
                  value={batchData.batchNumber}
                  onChange={e =>
                    setBatchData({ ...batchData, batchNumber: e.target.value })
                  }
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Quantity"
                  fullWidth
                  size="small"
                  type="number"
                  value={batchData.quantity}
                  onChange={e =>
                    setBatchData({
                      ...batchData,
                      quantity: e.target.value,
                    })
                  }
                  InputProps={{
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="MRP (₹)"
                  fullWidth
                  size="small"
                  type="number"
                  value={batchData.pricing.mrp}
                  onChange={e =>
                    setBatchData({
                      ...batchData,
                      pricing: {
                        ...batchData.pricing,
                        mrp: e.target.value,
                        sellingPrice: calculateSellingPrice(
                          e.target.value,
                          batchData.pricing.discount,
                        ),
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Discount (%)"
                  fullWidth
                  size="small"
                  type="number"
                  value={batchData.pricing.discount}
                  onChange={e =>
                    setBatchData({
                      ...batchData,
                      pricing: {
                        ...batchData.pricing,
                        discount: e.target.value,
                        sellingPrice: calculateSellingPrice(
                          batchData.pricing.mrp,
                          e.target.value,
                        ),
                      },
                    })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Selling Price (₹)"
                  fullWidth
                  size="small"
                  type="number"
                  value={batchData.pricing.sellingPrice}
                  onChange={e =>
                    setBatchData({
                      ...batchData,
                      pricing: {
                        ...batchData.pricing,
                        sellingPrice: e.target.value,
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Purchase Price (₹)"
                  fullWidth
                  size="small"
                  type="number"
                  value={batchData.pricing.purchasePrice}
                  onChange={e =>
                    setBatchData({
                      ...batchData,
                      pricing: {
                        ...batchData.pricing,
                        purchasePrice: e.target.value,
                      },
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    sx: {
                      borderRadius: "0.5rem",
                      fontFamily: "Outfit",
                    },
                  }}
                  InputLabelProps={{
                    sx: {
                      fontFamily: "Outfit",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBatchDialogOpen(false)}
            sx={{
              fontFamily: "Outfit",
              color: "#5652B0",
              "&:hover": { backgroundColor: "#F9F8FE" },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddBatch}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": { backgroundColor: "#4A47A3" },
              fontFamily: "Outfit",
            }}>
            Add Batch
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProductVariants;
